/**
 * React component that renders the "mini airbnb" list item for mobile view
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'reactstrap'
import Link from 'client/shared/js/components/Link'
import MiniCardListItem from '../MiniCardListItem'
import { Title, BtnLink } from '../MiniCardUIComponents'
import AirbnbCoverImage from 'client/app/js/components/AirbnbCoverImage'
import DotDivider from 'client/shared/js/components/DotDivider'
import { parsePrice } from 'client/shared/js/utils/airbnb'
import styles from './MiniAirbnbCardListItem.module.scss'

const MiniAirbnbCardListItem = ({ data, url, orderNum, onClick, onSeeDetailsClick }) => {
  const { title } = data

  const image = useMemo(() => (<AirbnbCoverImage data={data} />), [data])

  const header = useMemo(() => {
    return (
      <Button
        className={cx(styles['airbnb-btn'], 'round')}
        color='airbnb'>
        <FontAwesomeIcon className='font-inherit airbnb' icon={['fab', 'airbnb']} />
      </Button>
    )
  }, [])

  const body = useMemo(() => {
    const price = parsePrice(data.price)

    return (
      <div className='d-flex flex-column mt-auto'>
        <div className={cx(styles['line-divider'])} />
        <div className='d-flex align-items-center'>
          <div className='font-small'>
            <span className='font-weight-semibold'>{price}</span> / night
          </div>
          <DotDivider className={styles['dot']} />
          <div className='d-flex align-items-center'>
            <FontAwesomeIcon className={cx(styles['star'], 'font-inherit color-airbnb')} icon={['fas', 'star']} />
            <span className={cx('font-weight-semibold font-xs', {
              'dark-gray': !data.ratings
            })}>
              {data.ratings || '-'}
            </span>
          </div>
        </div>
      </div>
    )
  }, [data.price, data.ratings])

  const seeDetails = useMemo(() => {
    const detailsBtn = (
      <BtnLink title='See place details' onClick={onSeeDetailsClick}>
        See details
      </BtnLink>
    )

    return url ? (<Link to={url} anchor silent>{detailsBtn}</Link>) : detailsBtn
  }, [url, onSeeDetailsClick])

  return (
    <MiniCardListItem
      onClick={onClick}
      header={header}
      image={image}>
      <div className={cx(styles['item'])}>
        <Title>{title}</Title>
        {body}
        <span className={cx(styles['item-links'])}>
          {seeDetails}
        </span>
      </div>
    </MiniCardListItem>
  )
}

MiniAirbnbCardListItem.propTypes = {
  data: PropTypes.object.isRequired,
  url: PropTypes.string,
  orderNum: PropTypes.number,
  onClick: PropTypes.func,
  onSeeDetailsClick: PropTypes.func
}

MiniAirbnbCardListItem.defaultProps = {
  onClick: () => false,
  onSeeDetailsClick: () => false
}

export default MiniAirbnbCardListItem
